import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { StyleSheet, ScrollView, RefreshControl, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import MetaHead from '@/components/MetaHead';
import ReferralSettings from '@/components/Settings/ReferralSettings';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthStore, useModalStore } from '@/store';

const createStyles = (colors, isXS, insetBottom = 0, hideHeaderBorder = false) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.solid_color_primary,
    borderTopWidth: hideHeaderBorder ? 0 : 1,
    borderTopColor: hideHeaderBorder ? 'transparent' : colors.border_color_secondary,
  },
  contentWrapper: {
    width: '100%',
    flex: 1,
    flexDirection: 'column',
  },
  scrollContainer: {
    flex: 1,
    width: '100%',
  },
  scrollContainerContent: {
    width: '100%',
    paddingBottom: insetBottom + 40,
  },
  content: {
    // Keep the side margins inside the scrollable area.
    maxWidth: 880,
    padding: isXS ? 20 : 24,
    paddingTop: isXS ? 10 : 15,
    alignSelf: 'center',
    width: '100%',
  },
});

const ReferralPage = React.memo(() => {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const [isNearTop, setIsNearTop] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const styles = useMemo(
    () => createStyles(colors, isXS, insets.bottom, isNearTop),
    [colors, isXS, insets.bottom, isNearTop]
  );

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    // Bump key to force ReferralSettings to re-fetch
    setRefreshKey(k => k + 1);
    // Small delay so the spinner is visible
    setTimeout(() => setRefreshing(false), 600);
  }, []);

  return (
    <View style={styles.container}>
      <MetaHead
        title={t('pages.referral.title')}
        description={t('pages.referral.description')}
        noindex
      />
      <View style={styles.contentWrapper}>
        <ScrollView
          style={styles.scrollContainer}
          contentContainerStyle={styles.scrollContainerContent}
          showsVerticalScrollIndicator={false}
          onScroll={(e) => setIsNearTop(e.nativeEvent.contentOffset.y < 10)}
          scrollEventThrottle={16}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor={colors.text_color_secondary}
              colors={[colors.text_color_secondary]}
            />
          }
        >
          <View style={styles.content}>
            <ReferralSettings key={refreshKey} />
          </View>
        </ScrollView>
      </View>
    </View>
  );
});

export default function ReferralRoute() {
  const router = useRouter();
  const openModal = useModalStore(state => state.actions.openModal);
  const token = useAuthStore(state => state.token);
  const isTokenExpired = useAuthStore(state => state.isTokenExpired);
  const isAuthenticated = !!token && !isTokenExpired();
  const opened = useRef(false);

  // Anonymous visitors deep-linking to `/referral` get the auth CTA
  // (the referral program is a per-user reward), then we route them
  // back so the URL doesn't sit on an orphaned page.
  useEffect(() => {
    if (isAuthenticated) return;
    if (opened.current) return;
    opened.current = true;
    openModal('AuthCTAModal', {
      source: 'referral_route',
      title: 'Sign in to invite friends',
      subtitle: 'The referral program credits both you and your friends — but we need an account to attribute the rewards.',
      icon: 'gift',
    });
    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace('/app');
    }
  }, [isAuthenticated, openModal, router]);

  if (!isAuthenticated) return null;

  return <ReferralPage />;
}
